import {
  Box,
  Button,
  ChakraProvider,
  CloseButton,
  Divider,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";

import { Proposal } from "@/models/proposal.model";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { FaPen, FaRegCopy } from "react-icons/fa";
import {
  ProposalStatus,
  StatusRenderer,
} from "../ProposalTable/proposalStatusRenderer";

import {
  approveProposal,
  changeProposalStatusToPendingUserReview,
  changeUnicoProcessCorrections,
  getProposalById,
  updateExternalId,
  updateOnlyStatus,
  updateProposalDisbursementDate
} from "@/services/proposal.service";
import { formatPhone } from "@/utils/formatPhone";

import { createBioValidation } from "@/services/bioValidation.service";
import { getNaturalPersonById } from "@/services/naturalPerson.service";
import { useNavigate, useParams } from "react-router-dom";
import createTheme from "../../theme/theme.ts";
import BankAndPersonalDetailModal from "../BankAndPersonalDetailModal.ts";
import BankDetailModal from "../BankDetailModal";
import CustomDatePicker from "../CustomDatePicker";


interface ProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string | null;
}

export default function FixProposalModal({
  isOpen,
  onClose,
  proposalId,
}: ProposalModalProps) {
  const { id: paramId } = useParams<{ id: string }>();
  const finalId = proposalId ?? paramId ?? null;
  // const [activeTab, setActiveTab] = useState("");
  const [showOverlayModalBody, setShowOverlayModalBody] = useState(false);
  const [getFixProposalIten, setFixProposalIten] = useState("");
  const [externalId, setExternalId] = useState("");
  const [paymentDate, setPaymentDate] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [isLoading, setIsLoading] = useState(false);;
  const [isBankDetailFormOpen, setIsBankDetailFormOpen] = useState(false);
  const [isBankAndPersonalDetailFormOpen, setIsBankAndPersonalDetailFormOpen] = useState(false);
  const toast = useToast();

  const navigate = useNavigate();

  const computedIsOpen = isOpen ?? Boolean(paramId);
  const handleCloseModal = onClose ?? (() => {
    navigate("/propostas");
  });

  useEffect(() => {
    if (!finalId) return;
    let alive = true;
    setIsLoading(true);
    if (computedIsOpen) {
      setIsLoading(true);
      getProposalById(finalId)
        .then((data) => {
          if (alive) setProposal(data ?? null);
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (alive) setIsLoading(false);
        });
      return () => {
        alive = false;
      };
    }
  }, [computedIsOpen]);


  const permissoesBotoes = [
    { status: 'PENDING_FORMALIZATION', acoes: ['cancelar e desbloquear', 'trocar processo unico', 'alterar para pagamento'] },
    { status: 'PENDING_PAYMENT', acoes: ['cancelar e desbloquear', 'trocar processo unico', 'alterar para pagamento'] },
    { status: 'PENDING_USER_REVIEW', acoes: ['corrigir dados bancarios', 'corrigir proposta', 'corrigir dados biometricos'] },

  ];

  const isAcaoPermitida = (acao: string, statusAtual: ProposalStatus) => {

    const permissao = permissoesBotoes.find(p => p.status === statusAtual);
    return permissao?.acoes.includes(acao);
  }

  const handleBankAndPersonalDataClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    setIsBankAndPersonalDetailFormOpen(true);
  };
  const handleBankDataClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsBankDetailFormOpen(true);
  };
  const handleButtonFormalizationClick = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    onClose();
    try {
      const naturalPerson = await getNaturalPersonById(proposal?.naturalPersonId!);
      const request = {
        proposalId: proposal?.id,
        naturalPerson: naturalPerson,
      };

      await createBioValidation(request);
      toast({
        title: `Formalização criada com sucesso`,
        description: "A formalização foi criada com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error creating bio validation:", error);

      toast({
        title: `Erro ao criar formalização`,
        description: "Ocorreu um erro ao criar a formalização.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };
  const hasErrorCreateProposalBank = (customStatus: string): boolean => {
    let newCustomStatus: any = {};
    try {
      newCustomStatus = JSON.parse(customStatus && customStatus !== "" ? customStatus : "{}");
    } catch (e) {
      newCustomStatus = {};
    }
    console.error("Custom Status Array:", newCustomStatus);

    return (
      newCustomStatus?.confirmacao &&
      newCustomStatus?.confirmacao?.criacao_proposta_bancarizadora?.status === "erro"
    );
  };
  const hasErrorBiometricValidation = (customStatus: string): boolean => {
    let newCustomStatus: any = {};
    try {
      newCustomStatus = JSON.parse(customStatus && customStatus !== "" ? customStatus : "{}");
    } catch (e) {
      newCustomStatus = {};
    }
    console.error("Custom Status Array:", newCustomStatus);

    return (
      newCustomStatus?.confirmacao &&
      newCustomStatus?.confirmacao?.criacao_processo_seg_identidade?.status === "erro"
    );
  };
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copiado",
      description: "Texto copiado para a área de transferência.",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleCancelProposal = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    try {
      await updateOnlyStatus(proposal?.id!, ProposalStatus.CANCELED);

      toast({
        title: "Proposta Cancelada",
        description: "Enviamos a proposta para cancelamento.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao Cancelar",
        description: "Ocorreu um erro ao cancelar a proposta.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePayedProposal = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    try {
      await updateOnlyStatus(proposal?.id!, ProposalStatus.PAYED);
      toast({
        title: "Proposta Paga",
        description: "Enviamos a proposta para paga.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao Pagar Proposta",
        description: "Ocorreu um erro ao pagar a proposta.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };


  const handleProposalPendingUserReview = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    try {
      await updateOnlyStatus(proposal?.id!, ProposalStatus.PENDING_USER_REVIEW);
      toast({
        title: "Proposta em Revisão",
        description: "Enviamos a proposta para revisão do usuário.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao Alterar a Proposta",
        description: "Ocorreu um erro ao alterar a proposta.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateProposalExternalId = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    try {
      await updateExternalId(proposal?.id!, externalId);
      toast({
        title: "Id da Bancarizadora Atualizado",
        description:
          "Estamos atualizando o id da bancarizadora em nossa base de dados.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao Atualizar id da Bancarizadora",
        description:
          "Ocorreu um erro ao tentar atualizar o id da bancarizadora .",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateProposalPaymentDate = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    try {
      const newDate = new Date(paymentDate.toString());

      await updateProposalDisbursementDate(proposal?.id!, newDate);
      toast({
        title: "Data de Pagamento Atualizada",
        description:
          "Estamos atualizando a data do pagamento em nossa base de dados.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao Atualizar id da Bancarizadora",
        description:
          "Ocorreu um erro ao tentar atualizar o id da bancarizadora .",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // const getModalSize = () => (activeTab === "seguranca" ? "2xl" : "xl");

  const getFormattedPixKey = () => {
    const pixKey = proposal?.BankDetail?.pixKey ?? "Não informado";
    const pixKeyType = proposal?.BankDetail?.pixKeyType;

    if (pixKeyType === "DOCUMENT") return cpfFormatter(pixKey);
    if (pixKeyType === "PHONE") return formatPhone(pixKey);

    return pixKey;
  };



  const approveProsal = async () => {
    try {
      await approveProposal(proposal?.id!!);
      toast({
        title: "Proposta aprovada",
        description: "A proposta foi aprovada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error approving proposal:", error);
      toast({
        title: "Erro",
        description: "Erro ao aprovar proposta",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleChangeProposalStatusToPendingUserReview = async () => {
    if (!proposal?.id) {
      toast({
        title: "Erro",
        description: "ID da proposta não encontrado.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      await changeProposalStatusToPendingUserReview(proposal.id);
      toast({
        title: "Status da proposta atualizado!",
        description: "Os dados foram alterados com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao trocar Status da Proposta",
        description: "Ocorreu um erro inesperado.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  const handleChangeUnicoUnico = async () => {
    if (!proposal?.id) {
      toast({
        title: "Erro",
        description: "ID da proposta não encontrado.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      await changeUnicoProcessCorrections(
        proposal.id!,
        {
          document: proposal?.NaturalPerson?.document,
          name: proposal?.NaturalPerson?.name,
        }
      );
      toast({
        title: "Processo Único atualizado!",
        description: "Os dados foram alterados com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao trocar processo único",
        description: "Ocorreu um erro inesperado.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };


  const handleFixProposal = async (fixType: string) => {
    if (!proposal?.id) {
      toast({
        title: "Erro",
        description: "ID da proposta não encontrado.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFixProposalIten(fixType);
    setShowOverlayModalBody(true);
  };

  const theme = createTheme();
  return (
    <>
      <Modal isOpen={computedIsOpen} onClose={handleCloseModal} >
        <ModalOverlay />
        <ModalContent
          borderRadius={{ base: "20px", md: "20px" }}
          overflow="scroll"
          maxW={{ base: "90%", md: "90%", lg: "800px" }}
        >
          {isLoading ? (
            <ChakraProvider theme={theme}>
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                padding="3em"
              >
                <VStack spacing={4}>
                  <Spinner size="xl" color="primary" thickness="4px" speed="0.65s" />
                  <Text fontSize="xl" fontWeight="medium">
                    Carregando...
                  </Text>
                </VStack>
              </Box>
            </ChakraProvider>
          ) : !proposal ? (
            <Box p={6}>
              <Text>Proposta não encontrada.</Text>
            </Box>
          ) : (
            <>


              {/* Overlay ModalBody */}
              {showOverlayModalBody && (
                <ModalBody
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  bg="white"
                  zIndex={10}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="lg"
                >
                  <CloseButton
                    position="absolute"
                    top={4}
                    right={4}
                    onClick={() => setShowOverlayModalBody(false)}
                    size="lg"
                  />

                  {getFixProposalIten === "status" && (
                    <>
                      <Text fontSize="2xl" mb={4} textAlign="left" width={"100%"}>
                        Alterar status da Proposta
                      </Text>
                      <Text fontSize="sm" mb={4} textAlign="left" width={"100%"}>
                        Analise os status da proposta e escolha o mais adequado.
                      </Text>

                      <></>

                      <Divider borderColor="gray.500" borderWidth="1px" />



                      <Flex
                        justify="space-between"
                        flexDirection={{ base: "column", md: "row" }}
                        width={"100%"}
                      >
                        <Text
                          fontSize="sm"
                          mb={4}
                          textAlign="left"
                          mt={3}
                          width={"60%"}
                        >
                          Altere o status da proposta na granatech para cancelada
                        </Text>
                        <Button
                          bg="secondary"
                          color="white"
                          fontFamily="Lexend Deca"
                          borderRadius="md"
                          _hover={{ bg: "primary" }}
                          marginTop={4}
                          mb={4}
                          onClick={(event) => handleCancelProposal(event)}
                          px={{ base: 4, md: 8 }}
                          w={{ base: "100%", md: "auto" }}
                        >
                          Cancelar Proposta
                        </Button>
                      </Flex>

                      <Divider borderColor="gray.500" borderWidth="1px" />

                      <Flex
                        justify="space-between"
                        flexDirection={{ base: "column", md: "row" }}
                        width={"100%"}
                      >
                        <Text
                          fontSize="sm"
                          mb={4}
                          textAlign="left"
                          mt={3}
                          width={"60%"}
                        >
                          Altere o status da proposta na granatech para falha de
                          pagamento{" "}
                        </Text>
                        <Button
                          bg="secondary"
                          color="white"
                          fontFamily="Lexend Deca"
                          borderRadius="md"
                          _hover={{ bg: "primary" }}
                          marginTop={4}
                          onClick={() =>
                            handleChangeProposalStatusToPendingUserReview()
                          }
                          px={{ base: 4, md: 8 }}
                          w={{ base: "100%", md: "auto" }}
                          mb={4}
                        >
                          Falha de Pagamento
                        </Button>
                      </Flex>

                      <Divider borderColor="gray.500" borderWidth="1px" />

                      <Flex
                        justify="space-between"
                        flexDirection={{ base: "column", md: "row" }}
                        width={"100%"}
                      >
                        <Text
                          fontSize="sm"
                          mb={4}
                          textAlign="left"
                          mt={3}
                          width={"60%"}
                        >
                          Altere o status da proposta para PAGA na granatech
                        </Text>
                        <Button
                          bg="secondary"
                          color="white"
                          fontFamily="Lexend Deca"
                          borderRadius="md"
                          _hover={{ bg: "primary" }}
                          mb={4}
                          marginTop={4}
                          onClick={(event) => handlePayedProposal(event)}
                          px={{ base: 4, md: 8 }}
                          w={{ base: "100%", md: "auto" }}
                        >
                          Pago
                        </Button>
                      </Flex>

                      <Divider borderColor="gray.500" borderWidth="1px" />

                      <Flex
                        justify="space-between"
                        flexDirection={{ base: "column", md: "row" }}
                        width={"100%"}
                      >
                        <Text
                          fontSize="sm"
                          mb={4}
                          textAlign="left"
                          mt={3}
                          width={"60%"}
                        >
                          Altere o status da proposta para Aguardando revisão do usuário na granatech
                        </Text>
                        <Button
                          bg="secondary"
                          color="white"
                          fontFamily="Lexend Deca"
                          borderRadius="md"
                          _hover={{ bg: "primary" }}
                          mb={4}
                          marginTop={4}
                          onClick={(event) => handleProposalPendingUserReview(event)}
                          px={{ base: 4, md: 8 }}
                          w={{ base: "100%", md: "auto" }}
                        >
                          Revisão do Usuário
                        </Button>
                      </Flex>

                      <Divider borderColor="gray.500" borderWidth="1px" />









                    </>
                  )
                  }
                  {getFixProposalIten === 'dt_pgto' && (
                    <>
                      <Text fontSize="2xl" mb={4} textAlign="left" width={"100%"}>
                        Alterar Data de Pagamento
                      </Text>
                      <Text fontSize="sm" mb={4} textAlign="left" width={"100%"}>
                        Altere a data de pagamento da prosta e atualize o status para
                        PAGA
                      </Text>
                      <></>
                      <CustomDatePicker
                        dateRange={paymentDate}
                        onChange={(date) => setPaymentDate(date)}
                        selectsRange
                      />
                      <></>
                      <Button
                        bg="secondary"
                        color="white"
                        fontFamily="Lexend Deca"
                        borderRadius="md"
                        _hover={{ bg: "primary" }}
                        marginTop={4}
                        onClick={(event) => handleUpdateProposalPaymentDate(event)}
                        px={{ base: 4, md: 8 }}
                        w={{ base: "100%", md: "auto" }}
                      >
                        Alterar data de pagamento
                      </Button>
                    </>
                  )}
                  {getFixProposalIten === "id_bancarizadora" && (
                    <>
                      <Text fontSize="2xl" mb={4} textAlign="left" width={"100%"}>
                        Alterar o id da proposta na bancarizadora
                      </Text>
                      <Text fontSize="sm" mb={4} textAlign="left" width={"100%"}>
                        Utilize esse recurso caso o id da proposta na granatech não
                        esteja igual ao id da proposta na bancarizadora.
                      </Text>
                      <></>
                      <Input
                        placeholder="Digite o novo ID da Proposta"
                        bg="white"
                        height="3rem"
                        color="black"
                        fontFamily="Lexend Deca"
                        fontSize="1rem"
                        value={externalId}
                        onChange={(e) => setExternalId(e.target.value)}
                      />
                      <></>
                      <Button
                        bg="secondary"
                        color="white"
                        fontFamily="Lexend Deca"
                        borderRadius="md"
                        _hover={{ bg: "primary" }}
                        marginTop={4}
                        onClick={(event) => handleUpdateProposalExternalId(event)}
                        px={{ base: 4, md: 8 }}
                        w={{ base: "100%", md: "auto" }}
                      >
                        Atualizar ID da Proposta
                      </Button>
                    </>
                  )}

                  {getFixProposalIten === "help" && (
                    <Text fontSize="2xl" mb={4}>
                      Novo ModalBody Sobreposto:: {getFixProposalIten}
                    </Text>
                  )}
                </ModalBody>
              )}

              <ModalBody p={{ base: 4, md: 6 }}>
                <Flex
                  justify="space-between"
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Text
                    fontSize={{ base: "2", md: "4xl" }}
                    color="secondary"
                    fontFamily="Lexend Deca"
                    textAlign="left"
                    mb={2}
                  >
                    Correções da Proposta
                  </Text>

                  {/* <IconButton
              aria-label="Altarar Status"
              icon={<FaQuestionCircle />}
              ml={2}
              onClick={() => handleFixProposal('help')}
              size={{ base: "lg", md: "sm" }}
              variant="ghost"
              color="gray.500"
            /> */}
                </Flex>

                <>
                  <VStack spacing={1} align="stretch">
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Titular:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.NaturalPerson?.name ?? "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        CPF Titular:
                      </Text>
                      <>
                        <Text
                          fontSize={{ base: "md", md: "md" }}
                          fontFamily="Lexend Deca"
                          color="gray.500"
                        >
                          {proposal?.NaturalPerson?.document
                            ? cpfFormatter(proposal?.NaturalPerson?.document)
                            : "Não informado"}
                          <IconButton
                            aria-label="Copiar CPF"
                            icon={<FaRegCopy />}
                            onClick={() =>
                              handleCopy(
                                cpfFormatter(proposal?.NaturalPerson?.document!)
                              )
                            }
                            size={{ base: "xl", md: "sm" }}
                            variant="ghost"
                            color="gray.500"
                            alignSelf="center"
                          />
                        </Text>
                      </>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Bancarizadora:{" "}
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.chosenFinancialInfo?.Product?.processor?.name ||
                          "-"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Tabela:{" "}
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.chosenFinancialInfo?.Product?.name! || "-"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    {proposal?.BankDetail?.pixKeyType !== "BANK_ACCOUNT" && (
                      <>
                        <Flex
                          justify="space-between"
                          align="stretch"
                          flexDirection={{ base: "column", md: "row" }}
                        >
                          <Text
                            fontSize={{ base: "md", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            Chave Pix:
                          </Text>
                          <Text
                            fontSize={{ base: "md", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            {getFormattedPixKey()}
                          </Text>
                        </Flex>
                        <Divider borderColor="gray.500" borderWidth="1px" />
                      </>
                    )}

                    {proposal?.BankDetail?.pixKeyType === "BANK_ACCOUNT" && (
                      <>
                        <Flex
                          justify="space-between"
                          flexDirection={{ base: "column", md: "row" }}
                        >
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            Banco:
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            {proposal?.BankDetail?.bankCode ?? "Não informado"}
                          </Text>
                        </Flex>
                        <Divider borderColor="gray.500" borderWidth="1px" />

                        <Flex
                          justify="space-between"
                          flexDirection={{ base: "column", md: "row" }}
                        >
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            Agência:
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            {proposal?.BankDetail?.agency ?? "Não informado"}
                          </Text>
                        </Flex>
                        <Divider borderColor="gray.500" borderWidth="1px" />

                        <Flex
                          justify="space-between"
                          flexDirection={{ base: "column", md: "row" }}
                        >
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            Conta:
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            {proposal?.BankDetail?.account ?? "Não informado"}
                          </Text>
                        </Flex>
                        <Divider borderColor="gray.500" borderWidth="1px" />

                        <Flex
                          justify="space-between"
                          flexDirection={{ base: "column", md: "row" }}
                        >
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            Dígito Verificador:
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            {proposal?.BankDetail?.accountDigit ?? "Não informado"}
                          </Text>
                        </Flex>
                        <Divider borderColor="gray.500" borderWidth="1px" />
                      </>
                    )}
                    <Flex
                      justify="space-between"
                      width="100%"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Id da Proposta:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.id ?? "Não informado"}
                        <IconButton
                          aria-label="Copiar Número da Proposta"
                          icon={<FaRegCopy />}
                          ml={2}
                          onClick={() => handleCopy(proposal?.id!)}
                          size={{ base: "xl", md: "sm" }}
                          variant="ghost"
                          color="gray.500"
                        />
                      </Text>
                    </Flex>

                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      width="100%"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Número da Proposta:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.shortHash ?? "Não informado"}
                        <IconButton
                          aria-label="Copiar Número da Proposta"
                          icon={<FaRegCopy />}
                          ml={2}
                          onClick={() => handleCopy(proposal?.shortHash!)}
                          size={{ base: "xl", md: "sm" }}
                          variant="ghost"
                          color="gray.500"
                        />
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      width="100%"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Data da Proposta:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.NaturalPerson?.updatedAt
                          ? format(
                            proposal?.NaturalPerson.updatedAt,
                            "dd/MM/yyyy - HH:mm"
                          )
                          : ""}
                      </Text>
                    </Flex>

                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Data de Formalização:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.UnicoData?.lastFormalizationDate
                          ? format(
                            proposal?.UnicoData?.lastFormalizationDate,
                            "dd/MM/yyyy - HH:mm"
                          )
                          : ""}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        VALOR LIBERADO AO CLIENTE
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {BrlCurrencyFormatter(
                          proposal?.chosenFinancialInfo?.releasedAmount
                        )}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        ANOS ANTECIPADOS
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.anticipatedYears ?? "Erro ao obter anos"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        VALOR BLOQUEADO
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {BrlCurrencyFormatter(
                          proposal?.chosenFinancialInfo?.Installments?.slice(
                            0,
                            proposal?.anticipatedYears ?? undefined
                          ).reduce((acc, installment) => acc + installment.payment, 0)
                        )}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        TAXA A.M.
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {(proposal?.chosenFinancialInfo?.monthlyRate! * 100)?.toFixed(
                          2
                        ) ?? "XXXXX"}
                        %
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      width="100%"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Id Bancarizadora:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.externalId ?? "Não informado"}
                        <IconButton
                          aria-label="Copiar Número da Proposta"
                          icon={<FaRegCopy />}
                          ml={2}
                          onClick={() => handleCopy(proposal?.externalId!)}
                          size={{ base: "xl", md: "sm" }}
                          variant="ghost"
                          color="gray.500"
                        />
                        <IconButton
                          aria-label="Copiar Número da Proposta"
                          icon={<FaPen />}
                          ml={2}
                          onClick={() => handleFixProposal("id_bancarizadora")}
                          size={{ base: "xl", md: "sm" }}
                          variant="ghost"
                          color="red.500"
                        />
                      </Text>
                    </Flex>

                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Data do Pagamento:
                      </Text>

                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.disbursementDate
                          ? format(proposal?.disbursementDate, "dd/MM/yyyy - HH:mm")
                          : "-"}
                        <IconButton
                          aria-label="Copiar Número da Proposta"
                          icon={<FaPen />}
                          ml={2}
                          onClick={() => handleFixProposal("dt_pgto")}
                          size={{ base: "xl", md: "sm" }}
                          variant="ghost"
                          color="red.500"
                        />
                      </Text>
                    </Flex>


                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Status:
                      </Text>
                      <Flex
                        justify="right"
                        flexDirection={{ base: "column", md: "row" }}
                      >
                        <StatusRenderer
                          status={
                            ProposalStatus[
                            proposal?.status as keyof typeof ProposalStatus
                            ]
                          }
                          customStatus={proposal?.customStatus!}
                        />
                        <IconButton
                          aria-label="Altarar Status"
                          icon={<FaPen />}
                          ml={2}
                          onClick={() => handleFixProposal("status")}
                          size={{ base: "xl", md: "sm" }}
                          variant="ghost"
                          color="red.500"
                        />
                      </Flex>


                    </Flex>

                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Operações:
                      </Text>
                      <Flex
                        justify="flex-end"
                        flexDirection="row"
                        width="100%"
                        alignItems="center"
                        height="70px"
                      >
                        {isAcaoPermitida('cancelar e desbloquear', proposal?.status! as ProposalStatus) && (
                          <Button
                            bg="secondary"
                            color="white"
                            fontFamily="Lexend Deca"
                            borderRadius="md"
                            _hover={{ bg: "primary" }}
                            size="sm"
                            mx={1}
                            onClick={(event) => handleCancelProposal(event)}
                            px={4}
                            w="170px"
                            minW="170px"
                            fontSize="0.95rem"
                            whiteSpace="normal"
                            fontWeight="bold"
                            height="48px"
                          >
                            Cancelar e Desbloquear
                          </Button>
                        )}

                        {isAcaoPermitida('trocar processo unico', proposal?.status! as ProposalStatus) && (
                          <Button
                            bg="secondary"
                            color="white"
                            fontFamily="Lexend Deca"
                            borderRadius="md"
                            _hover={{ bg: "primary" }}
                            size="sm"
                            mx={1}
                            onClick={handleChangeUnicoUnico}
                            px={4}
                            w="170px"
                            minW="170px"
                            fontSize="0.95rem"
                            whiteSpace="normal"
                            fontWeight="bold"
                            height="48px"
                          >
                            Trocar Processo Único
                          </Button>
                        )}
                        {isAcaoPermitida('alterar para pagamento', proposal?.status! as ProposalStatus) && (
                          <Button
                            bg="secondary"
                            color="white"
                            fontFamily="Lexend Deca"
                            borderRadius="md"
                            _hover={{ bg: "primary" }}
                            size="sm"
                            mx={1}
                            onClick={() => approveProsal()}
                            px={4}
                            w="170px"
                            minW="170px"
                            fontSize="0.95rem"
                            whiteSpace="normal"
                            fontWeight="bold"
                            height="48px"
                            disabled={
                              !proposal?.UnicoData?.evidenceSet ||
                              !proposal?.UnicoData?.evidenceSet
                            }
                          >
                            Alterar para Pagamento
                          </Button>
                        )}

                        {isAcaoPermitida('corrigir proposta', proposal?.status! as ProposalStatus) && hasErrorCreateProposalBank(proposal?.customStatus!) && (
                          <Button
                            fontSize="0.94rem"
                            colorScheme="orange"
                            onClick={handleBankAndPersonalDataClick}
                            fontFamily="Lexend Deca"
                            size="sm"
                            minW="170px"
                            px={4}
                            whiteSpace="normal"
                            fontWeight="bold"
                            mx={1}

                          >
                            Corrigir Proposta
                          </Button>
                        )}
                        {isAcaoPermitida('corrigir dados biometricos', proposal?.status! as ProposalStatus) && hasErrorBiometricValidation(proposal?.customStatus!) && (
                          <Button
                            fontSize="0.94rem"
                            colorScheme="red"
                            onClick={handleButtonFormalizationClick}
                            fontFamily="Lexend Deca"
                            size="sm"
                            minW="170px"
                            px={4}
                            whiteSpace="normal"
                            fontWeight="bold"
                            mx={1}

                          >
                            Corrigir Dados Biométricos
                          </Button>
                        )}

                        {isAcaoPermitida('corrigir dados bancarios', proposal?.status! as ProposalStatus) && !hasErrorCreateProposalBank(proposal?.customStatus!) && (
                          <Button
                            fontSize="0.94rem"
                            colorScheme="green"
                            onClick={handleBankDataClick}
                            fontFamily="Lexend Deca"
                            size="sm"

                            px={4}
                            minW="auto"
                            whiteSpace="nowrap"
                            fontWeight="bold"
                          >
                            Corrigir Dados Bancários
                          </Button>
                        )}


                      </Flex>


                    </Flex>

                    <Divider borderColor="gray.500" borderWidth="1px" />
                  </VStack>
                </>
              </ModalBody>
            </>)}
        </ModalContent>

        { }
      </Modal>
      <BankDetailModal
        isOpen={isBankDetailFormOpen}
        onClose={() => setIsBankDetailFormOpen(false)}
        proposalId={proposal?.id}
      />
      <BankAndPersonalDetailModal
        isOpen={isBankAndPersonalDetailFormOpen}
        onClose={() => setIsBankAndPersonalDetailFormOpen(false)}
        proposalId={proposal?.id}
      />
    </>

  );
}
